<template>
    <RegularModal ref="modal" modal-card-class="is-large">
        <template v-slot:title>
            {{ translate("page.start_fights.finished_fights") }}
        </template>
        <template v-slot:body>
            <div class="columns" v-if="initDone">
                <div class="column">
                    <div class="mb-3" v-if="finishedFights.length > 0">
                        <QueryInput v-model="searchQuery" />
                    </div>

                    <table class="table is-condensed is-fullwidth" v-if="filteredFights.length > 0" @mouseleave="fightHover(0)">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{ translate("common.category") }}</th>
                                <th>{{ translate("common.round") }}</th>
                                <th>
                                    {{ translate("page.start_fights.competitor") }}
                                    #1
                                </th>
                                <th>
                                    {{ translate("page.start_fights.competitor") }}
                                    #2
                                </th>
                                <th>{{ translate("page.start_fights.winner") }}</th>
                                <th>{{ translate("page.start_fights.finished") }}</th>
                                <th />
                            </tr>
                        </thead>

                        <tbody>
                            <FightTableRow
                                @fightHover="fightHover"
                                :highlight-parameter-id="highlightParameterId"
                                v-for="(fight, index) in filteredFights"
                                :fight="fight"
                                :index="index"
                                :show-duplicated-pair="false"
                                :key="fight.id"
                            >
                                <template v-slot:afterCells>
                                    <td class="winner">
                                        <template v-if="fight.winnerCompetitor">
                                            <span class="clarity-mask">
                                                {{ fight.winnerCompetitor.firstName }}
                                                {{ fight.winnerCompetitor.lastName }}
                                            </span>
                                            <br />
                                            <small v-if="fight.winnerCompetitor.academy">
                                                {{ fight.winnerCompetitor.academy }}
                                            </small>
                                        </template>
                                    </td>
                                    <td>
                                        {{ convertToTimezone(fight.finished ?? "", eventTimezone) }}
                                    </td>
                                    <td>
                                        <button class="button is-small" @click="onRevertResult(fight.id)">
                                            <span class="icon">
                                                <i class="fa fa-undo" />
                                            </span>
                                            <span>{{ translate("page.start_fights.revert") }}</span>
                                        </button>
                                    </td>
                                </template>
                            </FightTableRow>
                        </tbody>
                    </table>
                    <div v-else-if="finishedFights.length === 0">
                        {{ translate("page.start_fights.no_fights_played") }}
                    </div>
                    <div v-else>No results for search criteria.</div>
                </div>
            </div>
            <div class="columns is-centered" v-else-if="requestsInProgress > 0">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <button @click="close" class="button">
                {{ translate("common.close") }}
            </button>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import FightTableRow from "./FightTableRow.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import fightsFilter from "../Common/fightsFilter";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import QueryInput from "../Common/QueryInput.vue";
    import fightsApi from "../Api/fights";
    import { OrganizerComplexFight } from "../Common/Fights/types";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const emit = defineEmits(["fightReverted"]);

    const searchQuery = ref("");
    const finishedFights = ref<OrganizerComplexFight[]>([]);
    const initDone = ref(false);
    const highlightParameterId = ref(0);
    const requestsInProgress = ref(0);

    const filteredFights = computed(() => {
        return fightsFilter.filter(finishedFights.value, searchQuery.value);
    });

    function fightHover(parameterId) {
        highlightParameterId.value = parameterId;
    }

    function fetchFinishedFights() {
        requestsInProgress.value++;
        return fightsApi
            .getFinishedFights(props.eventId, matId.value)
            .then(response => {
                finishedFights.value = response.result;
            })
            .finally(() => {
                requestsInProgress.value--;
            });
    }

    function revertResult(fightId) {
        return fightsApi.revertFightResult(props.eventId, fightId);
    }

    function onRevertResult(fightId) {
        const text = translate("page.start_fights.sure_to_revert") + " " + fightId + "?";
        if (!confirm(text)) {
            return;
        }

        return revertResult(fightId)
            .then(() => {
                emit("fightReverted", matId.value);
                close();
            })
            .catch(handleError);
    }

    const matId = ref(0);
    const modal = ref<InstanceType<typeof RegularModal> | null>(null);

    function open(matIdValue: number) {
        matId.value = matIdValue;
        searchQuery.value = "";
        finishedFights.value = [];
        initDone.value = false;
        modal.value?.open();

        return fetchFinishedFights()
            .then(() => {
                initDone.value = true;
            })
            .catch(err => {
                handleError(err);
                close();
            });
    }

    function close() {
        modal.value?.close();
    }

    defineExpose({ open, close });
</script>

<style scoped>
    .winner {
        color: #62c462;
    }
</style>
